import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { line, Color, LIST_PAGES, NEW_DOCUMENT_ID, Size, displayRevisionedNumber, documentPageMode, packingSlipProblem, purchaseOrderPills, purchaseOrderRevisionPill, pageKindLineText, t, type BannerPill, type PurchaseOrderAction, type PurchaseOrderAnswer, type ReceiveRequest, type ShipConfirmationRequest } from '@bms2/shared';
import { Button, TextInput, Textarea } from '../../components/controls';
import { useNotice } from '../../components/controls/useNotice';
import { Money } from '../../components/Money';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/modals/Modal';
import { ReasonWindow } from '../../components/modals/ReasonWindow';
import modalStyles from '../../components/modals/modal.module.css';
import { AccessDenied } from '../../shell/Fallbacks';
import { ConfirmationModal } from './ConfirmationModal';
import { BannerPills } from './BannerPills';
import { DocumentFilesSection } from './DocumentFilesSection';
import { vendorReleaseCompanies } from './releaseFiles';
import { DocumentViewPage, type DocumentLinesDescriptor } from './DocumentViewPage';
import { PurchaseOrderForm } from './PurchaseOrderForm';
import { ApprovalReason, SendHint } from './ApprovalReason';
import { QuoteVersions } from './QuotePanels';
import { PURCHASE_ORDERS_PATH, get, post, useApi } from './api';
import { refreshMenuCounts } from '../../shell/menuCounts';
import { RecordMessagesPanel } from '../mail/RecordMessagesPanel';
import { DeliveredPanel } from './DeliveredPanel';
import { StaleSendBanner, staleSendFor } from './StaleSendWarning';
import { useViewerZone } from '../../shell/ViewerZone';
import { ViewPdfLink, documentPdfPath, packingSlipPath } from './ViewPdfLink';
import { type ScreenFact } from './PageFacts';
import styles from './DocumentPage.module.css';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { useStatusResolver } from '../../shell/StatusResolver';
import { AccountJournalTile } from '../accounting/AccountJournalTile';
import { useDayText } from '../../shell/viewerDates';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { PurchasePriceQuestion, usePurchasePriceQuestion } from './PurchasePriceQuestion';

/** The footer's words per action; the banner draws the one status pill (decision 1072). */
const ACTION_LABEL: Record<PurchaseOrderAction, () => string> = {
  approve: () => t('po.action.approve'),
  reject: () => t('po.action.reject'),
  resubmit: () => t('po.action.resubmit'),
  send: () => t('po.action.send'),
  receive: () => t('po.action.receive'),
  confirmShipment: () => t('po.action.confirmShipment'),
  createBill: () => t('po.action.createBill'),
  close: () => t('po.action.close'),
  receiveUnsent: () => t('po.action.receiveUnsent'),
  void: () => t('po.action.void'),
};

type OpenModal = 'send' | 'markSent' | 'receive' | 'confirmShipment' | 'receiveUnsent' | 'reject' | 'void' | null;


/**
 * The Purchase Order's page (piece 22): the banner with its number, the two
 * axes as pills and the facts; the lines through the purchase column set with
 * the printed reference beside each item; the footer's one set of actions per
 * status; the margin working behind its super-admin control (decision 0375's
 * shape); the shipments a drop ship confirmed. Every action is a route the
 * server refuses on its own terms, and the page only offers what the status
 * allows.
 */
export function PurchaseOrderPage({ orderId, apiBaseUrl = '' }: { orderId: string; apiBaseUrl?: string }) {
  const dayText = useDayText();
  const resolveStatus = useStatusResolver();
  const zone = useViewerZone();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // 🔒 The URL carries the mode, so a link opens the mode it names (decision 0409); `new` is the Draft the + New action opens.
  const creating = orderId === NEW_DOCUMENT_ID;
  const editing = documentPageMode(params.get('mode'), orderId) === 'edit';
  const page = useApi<PurchaseOrderAnswer>(editing ? null : `${PURCHASE_ORDERS_PATH}/${orderId}`, apiBaseUrl);
  const [modal, setModal] = useState<OpenModal>(null);
  // 🔒 The banner's tone rides the notice, so a refusal reads as an error and never as a success (T2099).
  const [noticeBanner, showNotice, notice] = useNotice();
  const setNotice = (message: string) => showNotice({ tone: 'good', text: message });
  const setRefusal = (message: string) => showNotice({ tone: 'error', text: message });
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  // 🔒 Decision 1073: the receipt this modal sends is the administrator's override, carrying the reason typed first.
  // It is the override window's own answer, held apart from every other window's reason (decision 2870).
  const [overrideReason, setOverrideReason] = useState<string | null>(null);
  const overriding = overrideReason !== null;
  const [working, setWorking] = useState<readonly string[] | null>(null);
  const [workingOpen, setWorkingOpen] = useState(false);
  // Save and Send lands here with `act=send`, so the send opens on the saved order rather than on a form that has not written yet.
  const asked = params.get('act');
  const [askDone, setAskDone] = useState(false);
  const open: OpenModal = modal ?? (asked === 'send' && !editing && !askDone ? 'send' : null);
  const close = () => {
    setModal(null);
    setOverrideReason(null);
    setAskDone(true);
  };
  // 🔒 Decision 3141: the Send and Mark as Sent windows ask about each item this order pays more for than its item file says.
  const question = usePurchasePriceQuestion(orderId, open === 'send' || open === 'markSent', apiBaseUrl);
  const answers = question.adjustments.length > 0 ? { adjustPurchasePrices: question.adjustments } : {};
  // 🔒 Nothing issues the order until the read has settled and every row is answered (decision 3141).
  const questionHeld = question.status !== 'settled' || question.unanswered;
  const answerFirst = questionHeld ? { disabledReason: question.status === 'failed' ? t('po.priceRise.failed') : question.status === 'loading' ? t('po.priceRise.checking') : t('po.priceRise.answerFirst') } : {};
  // 🔒 A refusal of the issue reads inside the window it was pressed in, the floor's among them (decision 3140).
  const windowRefusal = notice?.tone === 'error' ? <AlertBanner tone="error" message={notice.text} action={{ label: t('modal.dismiss'), onAction: () => showNotice(null) }} /> : null;

  if (editing) return <PurchaseOrderForm orderId={creating ? null : orderId} apiBaseUrl={apiBaseUrl} />;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const act = async (path: string, body: unknown, ok?: string) => {
    setBusy(true);
    const answer = await post<unknown>(`${PURCHASE_ORDERS_PATH}/${orderId}/${path}`, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      setRefusal(answer.message);
      return false;
    }
    close();
    // 🔒 Decision 3072: an approval decided here leaves the menu's Approvals balloon at once.
    if (path === 'approve' || path === 'reject') refreshMenuCounts();
    // 🔒 An approval that sent the order says so in its own sentence (T223; decision 0713).
    const afterApproval = (answer.data as { afterApproval?: string | null } | null)?.afterApproval;
    // 🔒 A value the exempt form's box cut short is named beside the sent notice (T3447).
    const cut = (answer.data as { exemptionNotes?: string[] } | null)?.exemptionNotes ?? [];
    if (afterApproval) setNotice(afterApproval);
    else if (ok) setNotice([ok, ...cut].join(' '));
    page.reload();
    return true;
  };

  const toggleWorking = async () => {
    if (workingOpen) return setWorkingOpen(false);
    setWorkingOpen(true);
    if (working !== null) return;
    const answer = await get<{ lines: string[] }>(`${PURCHASE_ORDERS_PATH}/${orderId}/margin-working`, apiBaseUrl);
    setWorking(answer.ok ? answer.data.lines : [t('po.workingFailed')]);
  };

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('po.notFound')} onRetry={page.reload}>
      {(order) => {
        const facts: ScreenFact[] = [
          { label: t('po.fact.vendor'), value: order.vendorName, route: `/companies/${order.vendorCompanyId}` },
          { label: order.fulfillmentMethod === 'DropShip' ? t('po.fact.shipTo') : t('po.fact.receiveAt'), value: order.shipToLabel, route: `/sites/${order.shipToSiteId}` },
          { label: t('po.fact.deal'), value: order.dealName, route: order.dealId ? `/deals/${order.dealId}` : null },
          { label: t('po.fact.salesOrder'), value: order.salesOrderNumber },
          { label: t('po.fact.rfq'), value: order.sourceRfqNumber, route: order.sourceRFQId ? `/rfqs/${order.sourceRFQId}` : null },
          { label: t('document.fact.date'), value: order.documentDate },
          { label: t('po.fact.createdBy'), value: line(order.createdByName, '·', dayText(order.createdAt)) },
        ];
        // 🔒 Decision 1072: one pill, the order's linear status, in place of the approval, sent and receipt pills.
        // 🔒 Decision 1225: the pill reads Sending or Delivery Failed over a Sent whose send the host has not confirmed.
        const pills = purchaseOrderPills({ status: order.displayedStatus ?? order.status }, resolveStatus);
        const stale = staleSendFor('PurchaseOrder', order.staleSend, zone);
        // 🔒 Which revision the vendor holds and which is still a draft stands beside the method (T135; decision 0712).
        const revisionPill = purchaseOrderRevisionPill(order);
        const kinds: BannerPill[] = [{ text: order.fulfillmentMethod === 'DropShip' ? t('po.method.dropShip') : t('po.method.stock'), group: 'inactive' }, ...(revisionPill ? [revisionPill] : [])];
        /* 🔨 Edit reopens the form on an order that has gone nowhere; one already with the vendor edits as the NEXT REVISION, and the control says so rather than prompting afterwards (decision 0485). A receipt stands in the stock ledger, so a received order is frozen and says why. */
        const editAction = order.editVerdict === 'frozen' ? null : (
          <span data-po-edit={order.editVerdict} data-po-revises={order.nextRevision ?? ''}>
            <Button
              size={Size.Medium}
              color={Color.Navy}
              text={order.nextRevision ? [line(t('document.revise.action'), displayRevisionedNumber(order.purchaseOrderNumber, Number(order.nextRevision)))] : [t('document.edit.action')]}
              actions={[() => navigate(`${LIST_PAGES.PurchaseOrder.route}/${order.id}?mode=edit`)]}
              disabled={busy}
            />
          </span>
        );
        const submitsForApproval = order.approval?.sendAction === 'submitForApproval';
        const footer = (
          <>
            {order.editFrozenReason ? <span className={styles.sentence} data-po-frozen>{order.editFrozenReason}</span> : null}
            {order.actions.map((action) => (
              <Button
                key={action}
                size={Size.Medium}
                {...(action === 'approve' || action === 'send' || action === 'receive' || action === 'confirmShipment' ? { color: Color.Navy } : {})}
                /* 🔒 Send carries a second entry, Mark as sent, on the same control (decision 0436); the Button is a split button the moment it is given two. */
                /* 🔒 Send names the revision it sends where the order has been revised, so the draft is what goes (T135; decision 0712). */
                /* 🔒 And it reads Submit for approval where the gate's dry run would hold the order, and the press routes and holds (T223; decision 0713). */
                text={action === 'send' && submitsForApproval ? [t('approval.submitForApproval')] : action === 'send' ? [order.revision > 0 ? line(ACTION_LABEL[action](), '·', `${t('po.revision.label')} ${String(order.revision).padStart(2, '0')}`) : ACTION_LABEL[action](), t('document.action.markSent')] : [ACTION_LABEL[action]()]}
                disabled={busy}
                actions={
                  action === 'send' && submitsForApproval
                    ? [() => void act('send', {}, t('approval.submitted'))]
                    : action === 'send'
                    ? [() => setModal('send'), () => setModal('markSent')]
                    : [
                        () => {
                          if (action === 'approve') void act('approve', {}, t('po.approvedNotice'));
                          else if (action === 'resubmit') void act('resubmit', {}, t('po.resubmittedNotice'));
                          // 🔒 Close is the last forward move, on a received order alone (decision 1072).
                          else if (action === 'close') void act('close', {}, t('po.closedNotice'));
                          else if (action === 'receiveUnsent') setModal('receiveUnsent');
                          // 🔒 Create bill opens the Bill form on this order, which fills from what is left on it (T136; decision 0711).
                          else if (action === 'createBill') navigate(`${LIST_PAGES.Bill.route}/${NEW_DOCUMENT_ID}?vendorId=${encodeURIComponent(order.vendorCompanyId)}&purchaseOrderId=${encodeURIComponent(order.id)}`);
                          else setModal(action);
                        },
                      ]
                }
              />
            ))}
            <SendHint view={order.approval} />
            {/* 🔒 The one Download PDF construction every document page carries (piece 27; decision 0557); this page was the only one that had it. */}
            {/* 🔨 The sheet's own control moved to the action bar above both views, so one document offers it once (decision 0612). */}
          </>
        );
        /** 🔒 The order's lines as the one read-only table draws every kind's (decision 0612). */
        const lines: DocumentLinesDescriptor = {
          title: t('po.receipts.title'),
          rowAttribute: 'data-po-line',
          blockAttribute: 'data-po-lines',
          empty: t('po.emptyLines'),
          rows: order.lines.map((row) => ({
            id: row.id,
            label: row.itemName ? line(row.sku, '·', row.itemName) : row.description,
            note: row.referenceNote ? <span className={styles.reference} data-po-reference>{row.referenceNote}</span> : null,
            cells: [line(String(row.quantity), row.unitName, row.quantityConsumed > 0 ? `· ${row.quantityConsumed} ${t('po.lines.received')}` : null)],
            amount: <Money cents={row.amountCents} currencyCode={order.currencyCode} />,
          })),
          totals: [{ label: t('po.lines.total'), value: <Money cents={order.totalCents} currencyCode={order.currencyCode} />, attributes: { 'data-po-total': String(order.totalCents) } }],
        };
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(order);
        const notesPanel = <DocumentNotes recordType="PurchaseOrder" recordId={order.id} recordLabel={order.displayNumber} apiBaseUrl={apiBaseUrl} onError={setRefusal} carried={carried} />;
        // 🔒 The files attached to this PurchaseOrder, one block placed in the Details view (decision 0457; T1071).
        const filesPanel = <DocumentFilesSection recordType="PurchaseOrder" recordId={order.id} writeRefusal={order.writeRefusal ?? null} apiBaseUrl={apiBaseUrl} releaseTo={vendorReleaseCompanies(order.vendorCompanyId, order.vendorName)} />;
        // One Account Journal tile, placed in the Details view and handed to the PDF view's rail as its Journal tab (T1070; decision 1662).
        const journalPanel = <AccountJournalTile sourceDocumentType="PurchaseOrder" sourceDocumentId={order.id} apiBaseUrl={apiBaseUrl} />;
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            journal={journalPanel}
            documentType="PurchaseOrder"
            icon="accounting"
            title={order.displayNumber}
            subtitle={pageKindLineText({ page: 'document', documentType: 'PurchaseOrder' })}
            information={facts}
            kinds={<BannerPills pills={kinds} size={Size.Small} attribute="data-po-kinds" />}
            status={<BannerPills pills={pills} attribute="data-po-pills" hint={order.approval?.reason ?? null} />}
            footer={footer}
            footerAttributes={{ 'data-po-actions': order.actions.join(' ') }}
            pageAttributes={{ 'data-purchase-order': order.id, 'data-po-approval': order.approvalStatus, 'data-po-receipt': order.fulfillmentStatus, 'data-po-status': order.status }}
            print={order.printDocument ?? null}
            layout={order.printLayout ?? null}
            sheet={order.printSheet ?? null}
            writeRefusal={order.writeRefusal ?? null}
            /* 🔒 Decision 1072: the corner ribbon reads the one status in its own words, as the pill does. */
            ribbon={pills[0]?.text || order.status || null}
            ribbonStatus={order.displayedStatus ?? order.status ?? null}
            pdfPath={documentPdfPath(PURCHASE_ORDERS_PATH, orderId)}
            listRoute={LIST_PAGES.PurchaseOrder.route}
            {...(editAction === null ? {} : { editAction })}
            apiBaseUrl={apiBaseUrl}
            terms={{ documentId: order.id, onSaved: page.reload }}
            lines={lines}
            whatsNext={
              /* 🔒 The footer's own moves, named as the next step (decision 1732). */
              <WhatsNextBanner
                documentType="PurchaseOrder"
                status={order.status}
                disabled={busy}
                actions={{
                  ...(order.actions.includes('send') ? { send: () => (submitsForApproval ? void act('send', {}, t('approval.submitted')) : setModal('send')) } : {}),
                  ...(order.actions.includes('receive') ? { receive: () => setModal('receive') } : {}),
                  ...(order.actions.includes('createBill') ? { raiseBill: () => navigate(`${LIST_PAGES.Bill.route}/${NEW_DOCUMENT_ID}?vendorId=${encodeURIComponent(order.vendorCompanyId)}&purchaseOrderId=${encodeURIComponent(order.id)}`) } : {}),
                }}
              />
            }
            notices={
              <>
                {noticeBanner}
                {/* 🔒 The approval block says why the order rests where it does (T131; decision 0713); an older answer carries the escalation alone. */}
                {order.approval ? <ApprovalReason view={order.approval} /> : order.escalationReason ? <p className={styles.sentence} data-po-escalation>{line(t('po.escalation'), '·', order.escalationReason)}</p> : null}
                {order.rejectionReason ? <p className={styles.rejection} data-po-rejection>{line(t('po.rejection'), '·', order.rejectionReason)}</p> : null}
                {/* 🔒 Decision 1073: goods stand against an order the vendor was never sent; the receipt is kept and the page says so. */}
                {order.receivedUnsent ? <p className={styles.rejection} data-po-received-unsent>{t('po.receive.unsentWarning')}</p> : null}
              </>
            }
            details={
              <>
                {/* 🔒 What this document posted and where each amount was placed (decision 1075). */}
                {journalPanel}
              {/* 🔒 How the order's margin was worked out, folded away until it is pressed (decision 0409). */}
              {/* 🔒 Its own block, headed and with its control drawn at rest (decision 1891; T1253): the lines it was revealed from by hover are no longer in its card, and a block hiding its one control read as an empty tile. */}
              {order.offersMarginWorking ? (
                <section className={styles.block} data-po-working-block="true">
                  <h2 className={styles.heading}>{t('po.workingTitle')}</h2>
                  <div className={styles.working} data-po-working={workingOpen ? 'open' : 'closed'}>
                    <button type="button" className={styles.workingToggle} onClick={() => void toggleWorking()} aria-expanded={workingOpen} data-po-working-toggle="true">
                      <Icon meaning={workingOpen ? 'collapse' : 'expand'} size="inline" />
                      {workingOpen ? t('po.workingHide') : t('po.working')}
                    </button>
                    {workingOpen ? (
                      <ul className={styles.workingLines}>
                        {(working ?? []).map((text, i) => <li key={i}>{text}</li>)}
                      </ul>
                    ) : null}
                  </div>
                </section>
              ) : null}

              <section className={styles.block} data-po-shipments={order.shipments.length}>
                <h2 className={styles.heading}>{t('po.shipments.title')}</h2>
                {order.shipments.length === 0 ? <p className={styles.muted}>{t('po.shipments.none')}</p> : null}
                <DetailList look="separator">
                  {order.shipments.map((s) => (
                    <DetailRow
                      key={s.id}
                      data-po-shipment-origin={s.originLabel ?? 'none'}
                      lead={s.shipmentNumber}
                      /* 🔒 Where the stock left from, on the shipment's own answer (decision 0502); a drop ship moved none and says nothing. */
                      columns={[{ value: line(s.shippedDate, s.trackingNumber ? `· ${s.trackingNumber}` : null, s.originLabel ? `· ${s.originLabel}` : null) }]}
                      /*
                        * 🔨 The packing slip is the Shipment's own sheet, Region 9 off and no price on it, and it is
                        * offered on a CONFIRMED shipment alone (piece 27). A Draft carries no shipped date, no carrier
                        * and no tracking reference, and the route refuses one by the same sentence.
                        */
                      actions={packingSlipProblem(s.status) === null ? <ViewPdfLink documentType="Shipment" path={packingSlipPath(s.id)} apiBaseUrl={apiBaseUrl} /> : null}
                    >
                      <span className={styles.rowAmount}>{s.status}</span>
                    </DetailRow>
                  ))}
                </DetailList>
              </section>

              {/* 🔒 The sheets the vendor holds (decision 0490). The revision number was a label until this list existed. */}
              <section className={styles.block} data-po-versions-block={order.versions.length}>
                <h2 className={styles.heading}>{t('quote.versions.title')}</h2>
                <QuoteVersions key={`versions-${order.versions[0]?.versionNumber ?? 0}`} quoteId={order.id} rows={order.versions} apiBaseUrl={apiBaseUrl} onError={setRefusal} path={PURCHASE_ORDERS_PATH} prefix="po" documentType="PurchaseOrder" />
              </section>
              {/* 🔒 The mail sent about this order and its link's opens (decisions 0162, 0791). */}
              <RecordMessagesPanel recordType="PurchaseOrder" recordId={order.id} timeZone={zone} apiBaseUrl={apiBaseUrl} />
              {/* 🔒 Where the newest send stands, one line per recipient (decisions 1225, 1226). */}
              <DeliveredPanel delivery={order.delivery ?? null} timeZone={zone} />
              {filesPanel}
              {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
              {notesPanel}
              </>
            }
          >
              {open === 'receive' || open === 'confirmShipment' ? (
                <ConfirmationModal
                  key={open}
                  open
                  documentNumber={order.purchaseOrderNumber}
                  mode={open === 'receive' ? { kind: 'receive', lines: order.lines.map((l) => ({ id: l.id, label: l.itemName ? line(l.sku, '·', l.itemName) : l.description, quantity: l.quantity, quantityConsumed: l.quantityConsumed, unitName: l.unitName })) } : { kind: 'confirmShipment' }}
                  busy={busy}
                  onCancel={close}
                  onReceive={(request: ReceiveRequest) => void act('receive', overriding ? { ...request, overrideReason } : request, t('po.receivedNotice'))}
                  onConfirmShipment={(request: ShipConfirmationRequest) => void act('confirm-shipment', overriding ? { ...request, overrideReason } : request, t('po.confirmedNotice'))}
                />
              ) : null}
              {/* 🔒 Mark as sent: the order becomes the vendor's copy with nothing emailed, and it is confirmed because it is not undoable (decision 0436). */}
              {open === 'markSent' ? (
                <Modal
                  open
                  title={t('document.markSent.title')}
                  onCancel={close}
                  dataModal="po-mark-sent"
                  actions={[
                    { kind: 'primary', label: t('document.action.markSent'), onAct: () => void act('mark-sent', answers, t('document.markedSent')), disabled: busy || questionHeld, ...answerFirst },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  {windowRefusal}
                  <p className={modalStyles.fieldLabel}>{t('document.markSent.body')}</p>
                  <PurchasePriceQuestion question={question} />
                </Modal>
              ) : null}
              {open === 'send' ? (
                <Modal
                  open
                  title={t('po.send.title')}
                  onCancel={close}
                  dataModal="send-purchase-order"
                  actions={[
                    { kind: 'primary', label: stale ? t('document.staleSend.sendAnyway') : t('po.send.send'), onAct: () => void act('send', { email: contact.trim() || null, message: message.trim() || null, ...(stale ? { sendAnyway: true } : {}), ...answers }, t('po.send.sent')), disabled: busy || !contact.trim() || questionHeld, ...answerFirst },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  {windowRefusal}
                  {/* 🔒 A first send of an old-dated order warns, and Send Anyway is what the audit row records (decision 1227). */}
                  <StaleSendBanner
                    warning={stale}
                    onChangeDate={() => {
                      close();
                      navigate(`${LIST_PAGES.PurchaseOrder.route}/${order.id}?mode=edit`);
                    }}
                  />
                  <TextInput size={Size.Medium} label={t('po.send.email')} value={contact} onChange={setContact} type="email" required name="poEmail" />
                  <Textarea size={Size.Large} label={t('po.send.message')} value={message} onChange={setMessage} />
                  <PurchasePriceQuestion question={question} />
                  {/* 🔒 A revision re-sent shows where the last sheet's send stands (decision 1226). */}
                  <DeliveredPanel delivery={order.delivery ?? null} timeZone={zone} heading={t('document.delivery.previousTitle')} />
                </Modal>
              ) : null}
              {/* 🔒 Decision 1073: the administrator's override asks why first, then opens the one Receive screen carrying it. */}
              <ReasonWindow
                open={open === 'receiveUnsent'}
                title={t('po.action.receiveUnsent')}
                label={t('po.receive.overrideReason')}
                actLabel={t('po.receive.overrideContinue')}
                hint={t('po.receive.overrideHint')}
                name="overrideReason"
                busy={busy}
                onCancel={close}
                dataModal="receive-unsent-purchase-order"
                onAct={(reason) => {
                  setOverrideReason(reason);
                  setModal(order.fulfillmentMethod === 'DropShip' ? 'confirmShipment' : 'receive');
                }}
              />
              <ReasonWindow
                open={open === 'reject'}
                title={t('po.reject.title')}
                label={t('po.reject.reason')}
                actLabel={t('po.action.reject')}
                busy={busy}
                onCancel={close}
                refusal={notice?.tone === 'error' ? notice.text : null}
                dataModal="reject-purchase-order"
                onAct={(reason) => act('reject', { reason }, t('po.rejectedNotice'))}
              />
              <ReasonWindow
                open={open === 'void'}
                title={t('po.void.title')}
                label={t('po.void.reason')}
                actLabel={t('po.action.void')}
                hint={t('po.void.hint')}
                busy={busy}
                onCancel={close}
                refusal={notice?.tone === 'error' ? notice.text : null}
                dataModal="void-purchase-order"
                onAct={(reason) => act('void', { reason }, t('po.voidedNotice'))}
              />
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}

/** The route element: the order named in the path, inside the list-and-detail frame (decision 0408). */
export function PurchaseOrderRoute({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { id } = useParams();
  return <PurchaseOrderPage orderId={id ?? NEW_DOCUMENT_ID} apiBaseUrl={apiBaseUrl} />;
}
